/**
 * New Expandable Legend Creation Utility
 * 
 * This replaces the complex createExpandableLegendFromFocus with a simple utility
 * that works with the new expandable legend data structure from the backend.
 */

import type { ExpandableLegendSeries, ExpandableLegendTimestamp } from '../types';
import type { ExpandableLegend, ExpandableLegendItem } from '@/types/ui/expandable-legend.types';
import { isExpandableLegendEnabled } from '@/config/featureFlags';

export interface CreateExpandableLegendOptionsV2 {
  /** Expandable legend configuration from backend */
  expandableLegend?: ExpandableLegend;

  /** Merged timestamp items (backend + user markers) - if provided, used instead of expandableLegend.items */
  mergedItems?: ExpandableLegendItem[];

  /** Currently selected timestamp item IDs */
  selectedItemIds: Set<string>;

  /** Currently visible timestamp item IDs (for chart opacity) */
  visibleItemIds: Set<string>;

  /** Focus mode state */
  focusMode: boolean;

  /** Focused sensors per section */
  focusedSensors: Record<string, string>;

  /** Enabled sensors per section */
  enabledSensors: Record<string, boolean>;

  /** Sensor colors mapping */
  sensorColors: Record<string, string>;

  /** All tabs data for linked sources (optional) */
  allTabsData?: Record<string, any>;

  /** Whether timestamps data is currently loading */
  timestampsLoading?: boolean;
}

/**
 * Creates expandable legend series using the new backend structure
 * 
 * This function is much simpler than the original because:
 * - No need to search for "latest" in complex ways
 * - No need to manually merge user markers
 * - Direct access to structured data from backend
 */
export function createExpandableLegendSeriesV2(
  options: CreateExpandableLegendOptionsV2
): ExpandableLegendSeries[] {
  const {
    expandableLegend,
    mergedItems,
    selectedItemIds,
    visibleItemIds,
    focusMode,
    focusedSensors,
    enabledSensors,
    sensorColors,
    timestampsLoading = false
  } = options;

  // Use mergedItems if provided (includes user markers), otherwise fall back to expandableLegend.items
  const itemsToUse = mergedItems || expandableLegend?.items || [];

  // If feature flag is disabled or no expandable legend config, return empty
  if (!isExpandableLegendEnabled() || !expandableLegend?.enabled) {
    return [];
  }

  // Only generate expandable series when in focus mode OR when there are focused sensors
  if (!focusMode && Object.keys(focusedSensors).length === 0) {
    return [];
  }

  const expandableSeries: ExpandableLegendSeries[] = [];

  // Get sensors to process based on focus mode
  const sensorsToProcess: Array<[string, string]> = [];

  if (focusMode && selectedItemIds.size > 0) {
    // Focus mode with timestamps: Create expandable series for ALL enabled sensors
    Object.entries(enabledSensors).forEach(([sensorName, isEnabled]) => {
      if (isEnabled) {
        // Find which section this sensor belongs to (if it's focused, use that sectionId)
        const sectionId = Object.entries(focusedSensors).find(([_, name]) => name === sensorName)?.[0] || 'default';
        sensorsToProcess.push([sectionId, sensorName]);
      }
    });
  } else {
    // Normal focus mode: Only process focused sensors
    Object.entries(focusedSensors).forEach(([sectionId, sensorName]) => {
      if (sensorName && enabledSensors[sensorName]) {
        sensorsToProcess.push([sectionId, sensorName]);
      }
    });
  }

  // For each sensor to process, create an expandable series
  sensorsToProcess.forEach(([sectionId, sensorName]) => {
    if (!sensorName || !enabledSensors[sensorName]) {
      return;
    }

    // Convert expandable legend items to timestamp format (uses merged items if provided)
    const timestamps: ExpandableLegendTimestamp[] = itemsToUse.map((item: ExpandableLegendItem) => ({
      id: item.id,
      label: item.label,
      timestamp: item.value,
      enabled: selectedItemIds.has(item.id),
      visible: visibleItemIds.has(item.id),
      color: item.color
    }));

    // Check if this sensor is actually focused (not just enabled)
    const isActuallyFocused = Object.values(focusedSensors).includes(sensorName);

    const series: ExpandableLegendSeries = {
      id: `${sectionId}-${sensorName}`,
      label: sensorName,
      color: sensorColors[sensorName] || expandableLegend.defaultColor,
      isSelected: enabledSensors[sensorName],
      isFocused: isActuallyFocused,
      timestamps,
      timestampsLoading,
    };

    expandableSeries.push(series);
  });

  return expandableSeries;
}

/**
 * Simple toggle function for timestamp items (replaces complex "latest" logic)
 */
export function toggleTimestampInExpandableLegend(
  selectedItemIds: Set<string>,
  itemId: string,
  multiSelect: boolean = true
): Set<string> {
  const newSet = new Set(selectedItemIds);

  if (multiSelect) {
    // Multi-select: toggle in/out of set
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
  } else {
    // Single-select: replace entire set
    newSet.clear();
    newSet.add(itemId);
  }

  return newSet;
}

/**
 * Convert timestamp values to item IDs for backward compatibility
 * This helps during the migration period where some code still uses timestamp values
 */
export function convertTimestampValuesToItemIds(
  timestampValues: Set<string>,
  expandableLegend?: ExpandableLegend
): Set<string> {
  if (!expandableLegend?.items) {
    return new Set();
  }

  const itemIds = new Set<string>();
  
  expandableLegend.items.forEach(item => {
    if (timestampValues.has(item.value)) {
      itemIds.add(item.id);
    }
  });

  return itemIds;
}

/**
 * Convert item IDs to timestamp values for backward compatibility
 */
export function convertItemIdsToTimestampValues(
  itemIds: Set<string>,
  expandableLegend?: ExpandableLegend
): Set<string> {
  if (!expandableLegend?.items) {
    return new Set();
  }

  const timestampValues = new Set<string>();
  
  expandableLegend.items.forEach(item => {
    if (itemIds.has(item.id)) {
      timestampValues.add(item.value);
    }
  });

  return timestampValues;
}

/**
 * Legacy compatibility function - wraps the old interface with new implementation
 * This allows gradual migration of components
 */
export function createExpandableLegendFromFocusCompat(
  oldOptions: {
    focusMode: boolean;
    focusedSensors: Record<string, string>;
    selectedTimestampsSet?: Set<string>;
    visibleTimestampsSet?: Set<string>;
    verticalLines: any[];
    sensorColors: Record<string, string>;
    enabledSensors: Record<string, boolean>;
    includeLatest?: boolean;
  },
  expandableLegend?: ExpandableLegend
): ExpandableLegendSeries[] {
  
  if (!isExpandableLegendEnabled() || !expandableLegend) {
    // Fallback to original function if feature flag is off or no config
    // Import and use the original function here if needed
    return [];
  }

  // Convert old timestamp values to new item IDs
  const selectedItemIds = convertTimestampValuesToItemIds(oldOptions.selectedTimestampsSet || new Set(), expandableLegend);
  const visibleItemIds = convertTimestampValuesToItemIds(oldOptions.visibleTimestampsSet || new Set(), expandableLegend);

  return createExpandableLegendSeriesV2({
    expandableLegend,
    selectedItemIds,
    visibleItemIds,
    focusMode: oldOptions.focusMode,
    focusedSensors: oldOptions.focusedSensors,
    enabledSensors: oldOptions.enabledSensors,
    sensorColors: oldOptions.sensorColors
  });
}